import * as THREE from "three";
import CameraControls from "camera-controls";
import { Earcut } from "three/src/extras/Earcut.js";
import { ViewportGizmo } from "three-viewport-gizmo";

import { Vector3D } from "./lod2";
import operatorUtils from "./utils/operatorUtils";
import calcAreaUtils from "./utils/calcAreaUtils";

const parentId = "plot-3d";
const gizmoContainerId = "gizmo-container";

export class Plot3d {
    constructor({ cameraSettings, onCameraViewChanged }) {
        this.parentId = parentId;
        this._cameraSettings = cameraSettings;
        this._onCameraViewChanged = onCameraViewChanged;
        this._init();
    }

    _init() {
        CameraControls.install({ THREE });

        // Init camera
        const frustumSize = 100;
        const camera = new THREE.OrthographicCamera(
            frustumSize / -2,
            frustumSize / 2,
            frustumSize / 2,
            frustumSize / -2,
            -1000,
            1000
        );

        camera.up.set(0, 0, 1);

        // Retreive camera settings
        if (this._cameraSettings) {
            const { position, rotation, zoom } = this._cameraSettings;
            if (position) {
                const { x, y, z } = this._cameraSettings.position;
                camera.position.set(x, y, z);
            }

            if (rotation) {
                const { x, y, z } = this._cameraSettings.rotation;
                camera.rotation.set(x, y, z);
            }

            if (zoom) {
                camera.zoom = zoom;
            }
        }

        // Init scene and renderer
        const scene = new THREE.Scene();

        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.setClearColor(0xFFFFFF);
        const mount = document.getElementById(this.parentId);
        mount.appendChild(renderer.domElement);

        // Init Gizmo with the CameraControls
        const gizmo = new ViewportGizmo(camera, renderer, this._getGizmoConfig());
        const controls = new CameraControls(camera, renderer.domElement);

        // Set the events listeners
        gizmo.addEventListener("start", () => (controls.enabled = false));
        gizmo.addEventListener("end", () => (controls.enabled = true));
        gizmo.addEventListener("change", () => {
            controls.setPosition(...camera.position.toArray());
            if (this._onCameraViewChanged) {
                this._onCameraViewChanged(this);
            }
        });

        controls.addEventListener("update", () => {
            controls.getTarget(gizmo.target);
            gizmo.update();

            if (this._onCameraViewChanged) {
                this._onCameraViewChanged(this);
            }
        });

        // Set the target
        // Retreive camera settings
        if (this._cameraSettings?.target) {
            const { x, y, z } = this._cameraSettings.target;
            gizmo.target.set(x, y, z);
            controls.setTarget(...gizmo.target.toArray());
            camera.lookAt(gizmo.target);
        }

        const clock = new THREE.Clock();
        const animation = () => {
            // update controls
            if (controls.enabled && !gizmo.animating) {
                controls.update(clock.getDelta());
            }

            renderer.toneMapping = THREE.CineonToneMapping;
            renderer.render(scene, camera);

            // Render the Gizmo
            renderer.toneMapping = THREE.NoToneMapping;
            gizmo.render();
        };

        renderer.setAnimationLoop(animation);

        const onResize = () => {
            const w = mount.offsetWidth;
            const h = mount.offsetHeight;
            const aspect = w / h;
            const halfHeight = frustumSize / 2;
            const halfWidth = frustumSize * aspect / 2;
            camera.left = -halfWidth;
            camera.right = halfWidth;
            camera.top = halfHeight;
            camera.bottom = -halfHeight;

            camera.updateProjectionMatrix();
            renderer.setSize(w, h);

            gizmo.update();
        };

        // window.onresize = onResize;
        window.addEventListener("resize", onResize);

        onResize();

        this._addOriginSphere(scene);

        const light = new THREE.PointLight(0xffffff, 100000, 1000);
        light.position.set(0, 0, 100);
        scene.add(light);

        const light2 = new THREE.AmbientLight(0xffffff, 2);
        scene.add(light2);

        this.dispose = () => {
            renderer.dispose();
            light.dispose();
            light2.dispose();
            scene.traverse(object => {
                if (!object.isMesh) {
                    return;
                }

                // Dispose geometry
                if (object.geometry) {
                    object.geometry.dispose();
                }

                // Dispose material(s)
                if (object.material) {
                    if (Array.isArray(object.material)) {
                        object.material.forEach(m => {
                            if (m.map?.dispose) {
                                m.map.dispose();
                            }
                            m.dispose();
                        });
                    } else {
                        if (object.material.map?.dispose) {
                            object.material.map.dispose();
                        }
                        object.material.dispose();
                    }
                }
            });
            if (mount.contains(renderer.domElement)) {
                mount.removeChild(renderer.domElement);
            }
            window.removeEventListener("resize", onResize);
            // onWheelDispose();
            // orbitControlDispose();
        };

        this.mount = mount;
        this.renderer = renderer;
        this.scene = scene;
        this.camera = camera;
        this.controls = controls;
        this.gizmo = gizmo;
    }

    changeCameraLookAt(x, y, z) {
        this.controls.setTarget(...this.gizmo.target.set(x, y, z).toArray());
        this.camera.lookAt(this.gizmo.target);
    }

    _addOriginSphere(scene) {
        const position = new THREE.Vector3(0, 0, 0);
        const radius = 1.5;
        const geometry = new THREE.SphereGeometry(radius, 32, 32);
        const material = new THREE.MeshBasicMaterial({ color: new THREE.Color("green"), side: THREE.DoubleSide });
        const sphere = new THREE.Mesh(geometry, material);
        sphere.position.copy(position);
        scene.add(sphere);
        return sphere;
    }

    addResult(calcArea, sliceDist, numberU, numberV, numberW, getResultColor, shouldDrawPoint) {
        const { originX, originY, originZ,
            uaxisX, uaxisY, uaxisZ,
            vaxisX, vaxisY, vaxisZ } = calcArea;
        const origin = new Vector3D(originX, originY, originZ);
        const uAxis = new Vector3D(uaxisX, uaxisY, uaxisZ);
        const vAxis = new Vector3D(vaxisX, vaxisY, vaxisZ);
        const normal = Vector3D.cross(uAxis, vAxis);
        const wAxis = normal.scale((numberW - 1) * sliceDist / normal.length());

        const calculatePosition = (u, v, w) => origin
            .add(uAxis.scale(u / (numberU - 1)))
            .add(vAxis.scale(v / (numberV - 1)))
            .add(wAxis.scale(w / (numberW - 1)));

        const cachedShouldDrawPoints = [];
        const shouldDrawPointCached = (u, v, w) => {
            const index = w * numberV * numberU + v * numberU + u;
            const cachedResult = cachedShouldDrawPoints[index];
            if (cachedResult) {
                return cachedResult;
            }
            const result = shouldDrawPoint(index);
            cachedShouldDrawPoints[index] = result;
            return result;
        };

        const triangles = [];
        const addFace = (pointsIndices, color) => {
            const faceVertices = pointsIndices.map(indices => {
                const { u, v, w } = indices;
                return calculatePosition(u, v, w);
            });
            triangles.push({ vertices: [faceVertices[0], faceVertices[1], faceVertices[2]], color });
            triangles.push({ vertices: [faceVertices[0], faceVertices[2], faceVertices[3]], color });
        };

        for (let w = 0; w < numberW; w++) {
            for (let v = 0; v < numberV; v++) {
                for (let u = 0; u < numberU; u++) {
                    if (!shouldDrawPointCached(u, v, w)) {
                        continue;
                    }
                    const index = w * numberV * numberU + v * numberU + u;
                    const color = getResultColor(index);
                    // left face
                    if (u === 0 || !shouldDrawPointCached(u - 1, v, w)) {
                        const pointsIndices = [
                            { u: u - 0.5, v: v - 0.5, w: w - 0.5 },
                            { u: u - 0.5, v: v + 0.5, w: w - 0.5 },
                            { u: u - 0.5, v: v + 0.5, w: w + 0.5 },
                            { u: u - 0.5, v: v - 0.5, w: w + 0.5 },
                        ];
                        addFace(pointsIndices, color);
                    }
                    // right face
                    if (u + 1 === numberU || !shouldDrawPointCached(u + 1, v, w)) {
                        const pointsIndices = [
                            { u: u + 0.5, v: v - 0.5, w: w - 0.5 },
                            { u: u + 0.5, v: v + 0.5, w: w - 0.5 },
                            { u: u + 0.5, v: v + 0.5, w: w + 0.5 },
                            { u: u + 0.5, v: v - 0.5, w: w + 0.5 },
                        ];
                        addFace(pointsIndices, color);
                    }
                    // back face
                    if (v === 0 || !shouldDrawPointCached(u, v - 1, w)) {
                        const pointsIndices = [
                            { u: u - 0.5, v: v - 0.5, w: w - 0.5 },
                            { u: u + 0.5, v: v - 0.5, w: w - 0.5 },
                            { u: u + 0.5, v: v - 0.5, w: w + 0.5 },
                            { u: u - 0.5, v: v - 0.5, w: w + 0.5 },
                        ];
                        addFace(pointsIndices, color);
                    }
                    // front face
                    if (v + 1 === numberV || !shouldDrawPointCached(u, v + 1, w)) {
                        const pointsIndices = [
                            { u: u - 0.5, v: v + 0.5, w: w - 0.5 },
                            { u: u + 0.5, v: v + 0.5, w: w - 0.5 },
                            { u: u + 0.5, v: v + 0.5, w: w + 0.5 },
                            { u: u - 0.5, v: v + 0.5, w: w + 0.5 },
                        ];
                        addFace(pointsIndices, color);
                    }
                    // top face
                    if (w === 0 || !shouldDrawPointCached(u, v, w - 1)) {
                        const pointsIndices = [
                            { u: u - 0.5, v: v - 0.5, w: w - 0.5 },
                            { u: u + 0.5, v: v - 0.5, w: w - 0.5 },
                            { u: u + 0.5, v: v + 0.5, w: w - 0.5 },
                            { u: u - 0.5, v: v + 0.5, w: w - 0.5 },
                        ];
                        addFace(pointsIndices, color);
                    }
                    // bottom face
                    if (w + 1 === numberW || !shouldDrawPointCached(u, v, w + 1)) {
                        const pointsIndices = [
                            { u: u - 0.5, v: v - 0.5, w: w + 0.5 },
                            { u: u + 0.5, v: v - 0.5, w: w + 0.5 },
                            { u: u + 0.5, v: v + 0.5, w: w + 0.5 },
                            { u: u - 0.5, v: v + 0.5, w: w + 0.5 },
                        ];
                        addFace(pointsIndices, color);
                    }
                }
            }
        }

        const colorToVertices = new Map();
        triangles.forEach(triangle => {
            const { color, vertices } = triangle;
            if (!colorToVertices.has(color)) {
                colorToVertices.set(color, []);
            }
            colorToVertices.get(color).push(vertices);
        });

        const allColors = [...colorToVertices.keys()];
        const vertices = new Float32Array(allColors.map(color => colorToVertices.get(color)).flat(2).map(v => [v.x, v.y, v.z]).flat());
        const geometry = new THREE.BufferGeometry();
        geometry.computeVertexNormals();
        geometry.setAttribute("position", new THREE.BufferAttribute(vertices, 3));

        let coordinateIndex = 0;
        allColors.forEach((color, i) => {
            const numberOfCoordinatesForColor = colorToVertices.get(color).length * 3;
            // start, count, material Index
            geometry.addGroup(coordinateIndex, numberOfCoordinatesForColor, i);
            coordinateIndex += numberOfCoordinatesForColor;
        });

        const materials = allColors.map(color => new THREE.MeshStandardMaterial({
            color: new THREE.Color(color),
            side: THREE.DoubleSide,
        }));

        materials.forEach(material => {
            material.transparent = true;
            material.opacity = 0.7;
        });

        const group = new THREE.Group();

        const mesh = new THREE.Mesh(geometry, materials);
        group.add(mesh);

        this.scene.add(group);
        this.render();
        return group;
    }

    addResultBalls(calcArea, calculation, fieldArray, getResultColor, shouldDrawPoint) {
        const group = new THREE.Group();
        const { numberU, numberV, numberW, sliceDist } = calculation.overviewResult;
        const { originX, originY, originZ,
            uaxisX, uaxisY, uaxisZ,
            vaxisX, vaxisY, vaxisZ } = calcArea;
        const origin = new Vector3D(originX, originY, originZ);
        const uAxis = new Vector3D(uaxisX, uaxisY, uaxisZ);
        const vAxis = new Vector3D(vaxisX, vaxisY, vaxisZ);
        const normal = Vector3D.cross(uAxis, vAxis);
        const wAxis = normal.scale((numberW - 1) * sliceDist / normal.length());
        const minDistanceResultPoints = Math.min(
            uAxis.length() / (numberU - 1),
            vAxis.length() / (numberV - 1),
        );
        const radius = minDistanceResultPoints / 2;
        const geometry = new THREE.SphereGeometry(radius, 4, 4);
        for (let w = 0; w < numberW; w++) {
            for (let v = 0; v < numberV; v++) {
                for (let u = 0; u < numberU; u++) {
                    const index = w * numberV * numberU + v * numberU + u;
                    const value = fieldArray[index];
                    const position = origin
                        .add(uAxis.scale(u / (numberU - 1)))
                        .add(vAxis.scale(v / (numberV - 1)))
                        .add(wAxis.scale(w / (numberW - 1)));
                    if (!shouldDrawPoint(value, position)) {
                        continue;
                    }
                    const color = getResultColor(value, position);
                    const tPosition = new THREE.Vector3(position.x, position.y, position.z);
                    const material = new THREE.MeshBasicMaterial({ color: new THREE.Color(color) });
                    const sphere = new THREE.Mesh(geometry, material);
                    sphere.position.copy(tPosition);
                    group.add(sphere);
                }
            }
        }
        this.scene.add(group);
        this.render();
        return group;
    }

    addAntennas(sectors, antennas) {
        const group = new THREE.Group();
        sectors.forEach(sector => {
            const antenna = antennas.find(antenna => antenna.name === sector.antennaName);
            const antennaHeight = antenna?.apertureHeight ?? 3;
            // const radiusTop = 0;
            // const radiusBottom = antennaHeight / 2;
            // const radialSegments = 8;
            const sideLength = antennaHeight / 8;

            const geometry = new THREE.BoxGeometry(sideLength, antennaHeight, sideLength);
            //const geometry = new THREE.CylinderGeometry(radiusTop, radiusBottom, antennaHeight, radialSegments);

            const color = operatorUtils.getColor(sector?.operator);

            const material = new THREE.MeshBasicMaterial({ color: new THREE.Color(color) });
            const cone = new THREE.Mesh(geometry, material);

            // cylinder.rotation.x = Math.PT / 2;
            cone.position.set(sector.positionX, sector.positionY, sector.height + antennaHeight / 2);

            // now you can rotate the mesh if you want — rotation will occur around the bottom center
            // e.g. rotate the cylinder axis to point along Z:
            cone.rotation.x = Math.PI / 2;

            group.add(cone);
        });
        this.scene.add(group);
        this.render();
    }

    async addPlans(plans, planUrls, placementHeight) {
        const group = new THREE.Group();
        for (let i = 0; i < plans.length; i++) {
            const plan = plans[i];
            // describes position and rotation around top left corner
            const { metersPerPoint, rotationAngle, referencePointX, referencePointY, opacity } = plan;
            const planUrl = planUrls[i];
            const { width, height } = await this._getImageDimensions(planUrl);

            const map = new THREE.TextureLoader().load(planUrl);
            const planWidth = metersPerPoint * width;
            const planHeight = metersPerPoint * height;
            const geometry = new THREE.PlaneGeometry(planWidth, planHeight);
            const material = new THREE.MeshBasicMaterial({ map, toneMapped: false, side: THREE.DoubleSide, });
            material.transparent = true;
            material.opacity = opacity / 10;
            const mesh = new THREE.Mesh(geometry, material);
            const angle = (360 - rotationAngle) * Math.PI / 180;

            const centerPosition = new Vector3D(
                planWidth / 2 - referencePointX * metersPerPoint,
                - planHeight / 2 + referencePointY * metersPerPoint,
                placementHeight
            ).rotateAroundZ(angle);

            mesh.position.set(
                centerPosition.x,
                centerPosition.y,
                centerPosition.z,
            );
            mesh.rotation.z = angle;
            group.add(mesh);
        }
        this.scene.add(group);
        this.render();
        return group;
    }

    _getImageDimensions(base64Image) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                resolve({ width: img.width, height: img.height });
            };
            img.onerror = () => {
                reject(new Error("Invalid image data"));
            };
            img.src = base64Image;
        });
    }

    _getAreaVerticesVectors(calcArea) {
        const origin = new Vector3D(calcArea.originX, calcArea.originY, calcArea.originZ);
        return [
            origin,
            new Vector3D(origin.x + calcArea.vaxisX, origin.y + calcArea.vaxisY, origin.z + calcArea.vaxisZ),
            new Vector3D(
                origin.x + calcArea.vaxisX + calcArea.uaxisX,
                origin.y + calcArea.vaxisY + calcArea.uaxisY,
                origin.z + calcArea.vaxisZ + calcArea.uaxisZ,
            ),
            new Vector3D(origin.x + calcArea.uaxisX, origin.y + calcArea.uaxisY, origin.z + calcArea.uaxisZ),
        ];
    }

    _createBuildingBody(calcArea, baseHeight) {
        const group = new THREE.Group();
        if (calcAreaUtils.isVertical(calcArea)) {
            return group;
        }
        const areaVertices = this._getAreaVerticesVectors(calcArea);
        if (areaVertices.find(v => v.z < baseHeight)) {
            return group;
        }
        const allVertices = [...areaVertices, ...areaVertices.map(v => ({ ...v, z: baseHeight }))];
        // Indices for forming faces (triangles)
        const sides = [];
        for (let i = 0; i < areaVertices.length; i++) {
            const indices = [];
            // top left corner
            indices.push(i);
            // top right corner
            const trc = i + 1;
            indices.push(trc < areaVertices.length ? trc : 0);
            // bottom right corner
            const brc = i + areaVertices.length + 1;
            indices.push(brc < allVertices.length ? brc : areaVertices.length);
            // bottom left corner
            indices.push(i + areaVertices.length);
            sides.push(indices);
        }

        const outlineMaterial = new THREE.LineBasicMaterial({ color: 0x07132a, linewidth: 2 });
        sides.forEach(indices => {
            const vertices = indices.map(i => allVertices[i]);
            const outlineGeometry = new THREE.BufferGeometry().setFromPoints(vertices.map(v => new THREE.Vector3(v.x, v.y, v.z)));
            const outline = new THREE.LineLoop(outlineGeometry, outlineMaterial);
            group.add(outline);
        });
        return group;
    }

    addCalcArea(calcArea, baseAreaColor, baseHeight) {
        const group = new THREE.Group();
        const verticesVectors = this._getAreaVerticesVectors(calcArea);
        const vertices = new Float32Array(verticesVectors.map(p => [p.x, p.y, p.z]).flat());

        // Indices for forming faces (triangles)
        const indices = [
            0, 1, 2, // first triangle
            0, 2, 3 // second triangle
        ];

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
        geometry.setIndex(indices);
        geometry.computeVertexNormals(); // important for lighting

        const material = new THREE.MeshBasicMaterial({
            color: new THREE.Color(baseAreaColor),
            side: THREE.DoubleSide,
        });

        material.transparent = true;
        material.opacity = 0.3;

        const fill = new THREE.Mesh(geometry, material);
        group.add(fill);

        const outlineGeometry = new THREE.BufferGeometry().setFromPoints(verticesVectors.map(v => new THREE.Vector3(v.x, v.y, v.z)));
        const outlineMaterial = new THREE.LineBasicMaterial({ color: 0x07132a, linewidth: 2 });
        const outline = new THREE.LineLoop(outlineGeometry, outlineMaterial);
        group.add(outline);

        if (baseHeight !== undefined) {
            const buildingBody = this._createBuildingBody(calcArea, baseHeight);
            group.add(buildingBody);
        }

        this.scene.add(group);

        this.render();

        return { group, outline, fill, triangles: indices.map(i => verticesVectors[i]) };
    }

    addBuildings(buildings, buildingColor) {
        const group = new THREE.Group();
        const triangles = [];

        buildings.forEach(building => {
            building.forEach(element => {
                const verticesVectors = element.points.map(p => new Vector3D(p.x, p.y, p.z));

                const vertices = new Float32Array(verticesVectors.map(p => [p.x, p.y, p.z]).flat());

                // Indices for forming faces (triangles)
                const indices = this._triangulate3DPolygon(verticesVectors);
                const geometry = new THREE.BufferGeometry();
                geometry.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
                geometry.setIndex(indices);
                geometry.computeVertexNormals(); // important for lighting

                const material = new THREE.MeshStandardMaterial({
                    color: new THREE.Color(buildingColor),
                    side: THREE.DoubleSide,
                });

                material.transparent = true;
                material.opacity = 0.7;

                const fill = new THREE.Mesh(geometry, material);
                group.add(fill);

                const outlineGeometry = new THREE.BufferGeometry().setFromPoints(verticesVectors.map(v => new THREE.Vector3(v.x, v.y, v.z)));
                const outlineMaterial = new THREE.LineBasicMaterial({ color: 0xffd966, linewidth: 2 });
                const outline = new THREE.LineLoop(outlineGeometry, outlineMaterial);
                group.add(outline);
                triangles.push(...indices.map(i => verticesVectors[i]));
            });

        });

        this.scene.add(group);
        this.render();
        return { group, triangles };
    }

    render() {
        this.renderer.render(this.scene, this.camera);
    }

    _getGizmoConfig() {
        const size = 100;
        return {
            container: `#${gizmoContainerId}`,
            size: size,
            placement: "top-left",
            offset: {
                left: size - 50,
                top: size,
            },
        };
    }

    _triangulate3DPolygon(polygon) {
        // Turn the polygon to be lay flat on xy plane.
        const vertices = polygon.map(p => new Vector3D(p.x, p.y, p.z));
        // pick one point
        const [p1] = vertices;
        // farthest away point from v1
        const p2 = vertices.reduce((acc, val) => {
            const distance = val.subtract(p1).length();
            return distance > acc.distance ? { point: val, distance } : acc;
        }, { point: p1, distance: 0 }).point;
        // // farthest away point from v1 and v2
        const p3 = vertices.reduce((acc, val) => {
            const distance = val.subtract(p1).length() + val.subtract(p2).length();
            return distance > acc.distance ? { point: val, distance } : acc;
        }, { point: p1, distance: 0 }).point;
        // plane normal
        const v1 = p2.subtract(p1);
        const v2 = p3.subtract(p1);
        const normal = v1.cross(v2).normalize();
        // find rotation axis and angle to align normal to z axis
        const zAxis = new Vector3D(0, 0, 1);
        const axis = normal.cross(zAxis);
        const sinA = axis.length();
        const cosA = normal.dot(zAxis);
        const angle = Math.atan2(sinA, cosA);
        const u = axis.normalize();

        // Rodrigues' rotation
        const rotatePointAroundAxis = p => {
            // translate so p1 is origin
            const v = p.subtract(p1);
            const cosT = Math.cos(angle);
            const sinT = Math.sin(angle);
            const dotUV = u.dot(v);
            const crossUV = u.cross(v);

            const rotated = new Vector3D(
                v.x * cosT + crossUV.x * sinT + u.x * dotUV * (1 - cosT),
                v.y * cosT + crossUV.y * sinT + u.y * dotUV * (1 - cosT),
                v.z * cosT + crossUV.z * sinT + u.z * dotUV * (1 - cosT),
            );

            // translate back
            return rotated.add(p1);
        };

        const flatPolygon = vertices.map(point => rotatePointAroundAxis(point));
        const earcutInput = flatPolygon.map(p => [p.x, p.y]).flat();
        return Earcut.triangulate(earcutInput);
    }
}